import SEO from '@/components/SEO';
import BrandMarquee from '@/components/BrandMarquee';
import { site } from '@/data/content';
import { Phone, ArrowUpRight, Check, Mic, Voicemail, Headphones, Play, Shield, Users, PhoneCall, DollarSign, Globe, MessageSquare, Volume2, ShieldCheck, Users as UsersIcon, ChevronDown, ChevronUp, Zap, Settings } from 'lucide-react';
import { useState } from 'react';

const features = [
  { icon: PhoneCall, title: 'Inbound calls', text: 'Receive customer calls in the workspace with full context before you pick up.' },
  { icon: Mic, title: 'Real-time transcription', text: 'Every call is transcribed live so agents can search and review later.' },
  { icon: Voicemail, title: 'Voicemail to ticket', text: 'Missed calls become tickets with the voicemail attached.' },
  { icon: Shield, title: 'Call recording', text: 'Record, store, and review calls for QA and compliance.' },
  { icon: Play, title: 'Outbound calling', text: 'Call customers back from the same thread with one click.' },
  { icon: Headphones, title: 'Agent coaching', text: 'Supervisors can listen, whisper, or join calls for training.' },
];

const useCases = [
  { title: 'Sales calls', text: 'Schedule demos, answer objections, and close deals without switching tools.' },
  { title: 'Support escalations', text: 'Move complex tickets to voice while keeping the full chat history visible.' },
  { title: 'Account management', text: 'Call VIP customers and log the outcome directly in their conversation.' },
];

const comparison = [
  { feature: 'Voice inside inbox', zuri: true, softphone: false, others: 'partial' },
  { feature: 'Real-time transcription', zuri: true, softphone: true, others: 'add-on' },
  { feature: 'Call-to-ticket linking', zuri: true, softphone: false, others: 'partial' },
  { feature: 'Per-agent pricing', zuri: true, softphone: true, others: false },
  { feature: 'IVR with drag-and-drop builder', zuri: true, softphone: false, others: 'add-on' },
  { feature: 'Pre-built IVR templates', zuri: true, softphone: false, others: false },
  { feature: 'Number masking', zuri: true, softphone: false, others: 'add-on' },
  { feature: 'Text-to-speech (free)', zuri: true, softphone: false, others: 'add-on' },
  { feature: 'Conference calls (free)', zuri: true, softphone: false, others: 'add-on' },
  { feature: 'Voicemail-to-ticket', zuri: true, softphone: false, others: 'partial' },
  { feature: 'Agent coaching & monitoring', zuri: true, softphone: false, others: 'add-on' },
  { feature: 'Call recording (optional)', zuri: true, softphone: true, others: 'add-on' },
  { feature: 'No phone number option', zuri: true, softphone: false, others: false },
  { feature: 'Pay-as-you-go pricing', zuri: true, softphone: false, others: 'partial' },
  { feature: 'Multi-country numbers', zuri: true, softphone: false, others: 'limited' },
  { feature: 'USA number with no monthly fee', zuri: true, softphone: false, others: false },
  { feature: 'AI-powered call summaries', zuri: true, softphone: false, others: false },
  { feature: 'Context from inbox', zuri: true, softphone: false, others: 'partial' },
  { feature: 'Global phone numbers (8+ countries)', zuri: true, softphone: false, others: 'limited' },
];

const phoneNumbers = [
  { country: 'Uganda', setup: '$25', monthly: '$25/month', available: true },
  { country: 'Kenya', setup: '$25', monthly: '$25/month', available: true },
  { country: 'Nigeria', setup: '$25', monthly: '$25/month', available: true },
  { country: 'Ethiopia', setup: '$25', monthly: '$25/month', available: true },
  { country: 'South Africa', setup: '$25', monthly: '$25/month', available: true },
  { country: 'Tanzania', setup: '$25', monthly: '$25/month', available: true },
  { country: 'Rwanda', setup: '$25', monthly: '$25/month', available: true },
  { country: 'United States', setup: '$25', monthly: 'No monthly fee', available: true, highlight: true },
];

const additionalServices = [
  { service: 'Call recording', price: '$0.005/minute', description: 'Record, store, and review calls for quality assurance' },
  { service: 'Transcription', price: '$0.004/minute', description: 'AI-powered real-time speech-to-text' },
];

const freeVoiceFeatures = [
  { 
    icon: Volume2, 
    title: 'Text to speech', 
    description: 'Convert text into natural-sounding audio in various languages and voices. Perfect for automated responses and accessibility.',
    link: '/text-to-speech'
  },
  { 
    icon: UsersIcon, 
    title: 'Conferences', 
    description: 'Connect people in multiparty calls or use conferences to combine all types of calls. Ideal for team discussions and customer escalations.',
    link: '/conferences'
  },
  { 
    icon: Phone, 
    title: 'IVR menus', 
    description: 'Dynamically build your IVRs to help answer and filter calls without the need for a human agent. Reduce wait times and improve efficiency.',
    link: '/ivr-menus'
  },
  { 
    icon: ShieldCheck, 
    title: 'Number masking', 
    description: 'Mask phone numbers to anonymize voice calls, keeping personal numbers safe. Protect privacy while maintaining communication.',
    link: '/number-masking'
  },
];

const faqs: Array<{ question: string; answer: string }> = [
  {
    question: 'What countries does Zuri Voice support?',
    answer: 'Zuri Voice supports domestic rates in over 60 countries. Phone numbers are available in Uganda, Kenya, Nigeria, Ethiopia, South Africa, Tanzania, Rwanda, and the United States. We continue to expand our global coverage.'
  },
  {
    question: 'How does the pay-as-you-go pricing work?',
    answer: 'You only pay for the minutes you use. There are no monthly commitments or minimum charges. Domestic calls start at $0.008/minute for receiving and $0.01/minute for making calls. Live chat widget and in-app calls are even more affordable at $0.005/minute (receive) and $0.007/minute (make).'
  },
  {
    question: 'What are the costs for phone numbers?',
    answer: 'Phone numbers cost $25 one-time setup fee with a $25 monthly maintenance fee in Uganda, Kenya, Nigeria, Ethiopia, South Africa, Tanzania, and Rwanda. In the United States, phone numbers have a $25 setup fee with no monthly maintenance fee, making it the most affordable option for US presence.'
  },
  {
    question: 'Can I use Zuri Voice without a phone number?',
    answer: 'Yes! You can use Zuri Voice with live chat widget and in-app agent calls without needing a traditional phone number. This is perfect for businesses that primarily communicate through digital channels. These calls require no phone number and are priced at $0.005/minute (receive) and $0.007/minute (make).'
  },
  {
    question: 'Are recording and transcription included?',
    answer: 'Call recording costs $0.005/minute and transcription costs $0.004/minute. These are optional add-ons that you can enable based on your needs. Both are highly recommended for quality assurance and compliance.'
  },
  {
    question: 'What features are included for free?',
    answer: 'Text-to-speech, conferences, IVR menus, and number masking are all included free of charge. These advanced features help you automate workflows, improve customer experience, and protect privacy without additional costs.'
  },
  {
    question: 'How does IVR work with Zuri Voice?',
    answer: 'Zuri Voice includes a drag-and-drop IVR builder with pre-built templates for common scenarios like sales routing, support departments, and after-hours handling. You can create custom call flows without coding and update menus dynamically.'
  },
  {
    question: 'How does call recording and transcription work?',
    answer: 'Calls are automatically recorded and transcribed in real-time using AI. Transcripts are available within seconds of the call ending and are searchable. Recordings are encrypted and stored securely for compliance and quality assurance.'
  },
  {
    question: 'Is Zuri Voice suitable for enterprise businesses?',
    answer: 'Absolutely. Zuri Voice scales to support enterprise needs with carrier-grade infrastructure, 99.95% uptime target, and dedicated engineering support. We offer custom solutions for large-scale deployments.'
  },
];

export default function ZuriVoice() {
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  return (
    <>
      <SEO
        title="Zuri Voice - AI-Powered Business Phone System | Missed Call Recovery & Pay-as-you-go Calling"
        description="Transform customer calls with AI transcription, recording, and missed call recovery. Pay-as-you-go pricing from $0.008/min. Available in Uganda, Kenya, Nigeria, Ethiopia, South Africa, Tanzania, Rwanda, and USA."
        path="/zuri-voice"
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-32 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5 text-sm font-semibold text-brand-300 backdrop-blur">
                <Phone className="h-4 w-4" /> Available in 8 countries
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Voice calls that <span className="gradient-text">live in your inbox</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                Make and receive calls with AI transcription, recording, and smart routing. Pay-as-you-go pricing from $0.008/min. No monthly commitments.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100 shadow-xl shadow-white/10"
                >
                  Start Free Trial <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="#pricing"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:border-brand-500 hover:bg-slate-800"
                >
                  View Pricing <DollarSign className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-600/20 to-brand-400/10 blur-3xl" />
              <div className="relative overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/60 p-2 backdrop-blur">
                <img
                  src="https://media.getzurihub.com/Jayrah%20Assets/Voice%20Zuri.avif"
                  alt="Zuri Voice platform interface"
                  className="rounded-2xl w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">Voice, made useful</h2>
            <p className="section-subtitle mx-auto mt-4">Make and take calls with context, transcription, and coaching.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="why-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="why-heading" className="section-title">Why choose Zuri Voice</h2>
            <p className="section-subtitle mx-auto mt-4">Built for modern teams that need more than just a phone system.</p>
          </div>
          
          <div className="grid gap-8 lg:grid-cols-2">
            <div className="space-y-6">
              {[
                {
                  icon: Zap,
                  title: 'AI-powered every step',
                  description: 'Real-time transcription, smart call summaries, and AI-suggested responses help your team work faster and smarter.',
                },
                {
                  icon: MessageSquare,
                  title: 'Seamless inbox integration',
                  description: 'Calls live alongside chats, emails, and social messages. Full customer context before you pick up the phone.',
                },
                {
                  icon: DollarSign,
                  title: 'Pay only for what you use',
                  description: 'No monthly commitments, no hidden fees. Starting at $0.008/min with transparent pricing that scales with your business.',
                },
              ].map((item) => (
                <div key={item.title} className="flex gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-100 text-brand-600">
                    <item.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">{item.title}</h3>
                    <p className="mt-1 text-sm text-slate-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="space-y-6">
              {[
                {
                  icon: Globe,
                  title: 'Global coverage, local presence',
                  description: 'Phone numbers in 8 countries across Africa and the US. Expand your reach without local infrastructure.',
                },
                {
                  icon: ShieldCheck,
                  title: 'Enterprise-grade reliability',
                  description: '99.95% uptime target, encrypted recordings, and carrier-grade infrastructure built for business.',
                },
                {
                  icon: Users,
                  title: 'Designed for teams',
                  description: 'Agent coaching, call monitoring, and collaborative tools help your team deliver exceptional customer experiences.',
                },
              ].map((item) => (
                <div key={item.title} className="flex gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-100 text-brand-600">
                    <item.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">{item.title}</h3>
                    <p className="mt-1 text-sm text-slate-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="how-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 id="how-heading" className="section-title">How it works</h2>
              <p className="mt-4 text-lg text-slate-600">
                Zuri Voice connects a phone number or SIP trunk to the workspace. Incoming calls are matched to the customer record, and agents can call out with one click from any conversation.
              </p>
              <ol className="mt-8 space-y-4">
                <li className="flex items-start gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-700">1</span>
                  <span className="text-slate-700">Connect your number or provision one through Zuri.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-700">2</span>
                  <span className="text-slate-700">Answer or place calls from inside the conversation thread.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-700">3</span>
                  <span className="text-slate-700">Review transcription, recording, and notes in the same view.</span>
                </li>
              </ol>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-white p-2 shadow-xl">
              <img
                src="https://media.getzurihub.com/Jayrah%20Assets/AI%20CALL.avif"
                alt="AI-powered call interface with transcription"
                className="rounded-2xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="missed-call-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div className="order-2 lg:order-1">
              <div className="rounded-3xl border border-slate-200 bg-white p-2 shadow-xl">
                <img
                  src="https://media.getzurihub.com/Jayrah%20Assets/Missed%20Call%20.avif"
                  alt="Missed call recovery feature"
                  className="rounded-2xl w-full h-auto"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 id="missed-call-heading" className="section-title">Missed Call Recovery</h2>
              <p className="mt-4 text-lg text-slate-600">
                Never lose a customer because you couldn't answer the phone. Automatically convert missed calls into tickets and follow up faster.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  {
                    title: 'Automatic ticket creation',
                    description: 'Missed calls automatically become support tickets with caller details and call time logged.',
                  },
                  {
                    title: 'Voicemail to text',
                    description: 'AI transcribes voicemails so agents can read and respond without listening to recordings.',
                  },
                  {
                    title: 'Smart follow-up',
                    description: 'Get notified instantly and reply via SMS, email, or chat to reconnect with customers.',
                  },
                  {
                    title: 'No more lost opportunities',
                    description: 'Turn missed calls into second chances. Every caller gets a follow-up, every time.',
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-100 text-brand-600">
                      <Phone className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900">{item.title}</h3>
                      <p className="mt-1 text-sm text-slate-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="ivr-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="ivr-heading" className="section-title">Easy IVR with ready-to-use templates</h2>
            <p className="section-subtitle mx-auto mt-4">Build intelligent call flows in minutes with our drag-and-drop IVR builder and pre-built templates.</p>
          </div>
          
          <div className="grid gap-8 lg:grid-cols-2">
            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Simple setup, powerful results</h3>
              <div className="space-y-4">
                {[
                  {
                    title: 'Drag-and-drop builder',
                    description: 'Create custom IVR flows without coding. Connect call paths, set up menu options, and configure routing rules visually.',
                  },
                  {
                    title: 'Pre-built templates',
                    description: 'Get started faster with templates for common scenarios: sales routing, support departments, after-hours handling, and more.',
                  },
                  {
                    title: 'Dynamic menu options',
                    description: 'Update IVR menus on the fly. Add seasonal prompts, temporary redirects, or emergency announcements without downtime.',
                  },
                  {
                    title: 'Smart call routing',
                    description: 'Route calls based on time of day, caller location, or customer segment. Ensure every caller reaches the right team instantly.',
                  },
                ].map((item) => (
                  <div key={item.title} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <h4 className="font-bold text-slate-900">{item.title}</h4>
                    <p className="mt-2 text-sm text-slate-600">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="rounded-3xl bg-slate-900 p-8 text-white shadow-xl">
              <h3 className="font-bold mb-6">Popular IVR templates</h3>
              <div className="space-y-4">
                {[
                  { name: 'Sales & Support Split', description: 'Route calls to sales or support based on customer selection' },
                  { name: 'Department Routing', description: 'Direct callers to billing, technical support, or general inquiries' },
                  { name: 'After-Hours Handler', description: 'Automated menu for non-business hours with voicemail option' },
                  { name: 'Multi-language Support', description: 'Offer language selection before connecting to agents' },
                ].map((template) => (
                  <div key={template.name} className="flex items-start gap-3 rounded-xl bg-slate-800 p-4">
                    <Settings className="mt-0.5 h-5 w-5 text-brand-400 shrink-0" />
                    <div>
                      <p className="font-semibold">{template.name}</p>
                      <p className="text-sm text-slate-400 mt-1">{template.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="usecases-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 id="usecases-heading" className="text-center section-title">Use cases</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            {useCases.map((u) => (
              <div key={u.title} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <h3 className="font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BrandMarquee />

      <section className="bg-slate-50 py-24" aria-labelledby="compare-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 id="compare-heading" className="mb-10 text-center section-title">How we compare</h2>
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left font-bold text-slate-700">Feature</th>
                  <th className="px-6 py-4 text-center font-bold text-brand-700">Zuri Voice</th>
                  <th className="px-6 py-4 text-center font-bold text-slate-700">Softphone</th>
                  <th className="px-6 py-4 text-center font-bold text-slate-700">Others</th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((row) => (
                  <tr key={row.feature} className="border-t border-slate-100">
                    <td className="px-6 py-4 font-medium text-slate-900">{row.feature}</td>
                    <td className="px-6 py-4 text-center"><Check className="mx-auto h-5 w-5 text-green-600" /></td>
                    <td className="px-6 py-4 text-center">{row.softphone === true ? <Check className="mx-auto h-5 w-5 text-green-600" /> : <span className="text-xs font-bold text-slate-500 uppercase">{row.softphone}</span>}</td>
                    <td className="px-6 py-4 text-center">{row.others === true ? <Check className="mx-auto h-5 w-5 text-green-600" /> : <span className="text-xs font-bold text-slate-500 uppercase">{row.others}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="pricing-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="pricing-heading" className="section-title">Phone numbers by country</h2>
            <p className="section-subtitle mx-auto mt-4">Get local presence in 8 countries across Africa and the United States.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {phoneNumbers.map((phone) => (
              <div
                key={phone.country}
                className={`rounded-2xl border bg-white p-6 text-center shadow-sm transition hover:-translate-y-1 hover:shadow-lg ${phone.highlight ? 'border-brand-500 ring-2 ring-brand-200' : 'border-slate-200'}`}
              >
                <div className="font-bold text-slate-900">{phone.country}</div>
                <div className="mt-2 text-sm text-slate-600">{phone.setup} setup</div>
                <div className={`mt-1 text-sm font-semibold ${phone.highlight ? 'text-brand-600' : 'text-slate-700'}`}>{phone.monthly}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="additional-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="additional-heading" className="section-title">Additional services</h2>
            <p className="section-subtitle mx-auto mt-4">Optional add-ons to enhance your voice experience.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2">
            {additionalServices.map((service) => (
              <div key={service.service} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-slate-900">{service.service}</h3>
                  <span className="text-sm font-bold text-brand-600">{service.price}</span>
                </div>
                <p className="mt-2 text-sm text-slate-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="free-features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="free-features-heading" className="section-title">Included for free</h2>
            <p className="section-subtitle mx-auto mt-4">Advanced features that cost extra elsewhere, included at no additional cost.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {freeVoiceFeatures.map((feature) => (
              <div key={feature.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                  <feature.icon className="h-6 w-6" />
                </div>
                <h3 className="font-bold text-slate-900">{feature.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="faq-heading" className="mb-10 text-center section-title">Frequently asked questions</h2>
          <div className="space-y-4">
            {faqs.map((faq) => (
              <div key={faq.question} className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                <button
                  onClick={() => setOpenFaq(openFaq === faq.question ? null : faq.question)}
                  className="flex w-full items-center justify-between p-6 text-left"
                >
                  <span className="font-semibold text-slate-900">{faq.question}</span>
                  {openFaq === faq.question ? <ChevronUp className="h-5 w-5 text-slate-500" /> : <ChevronDown className="h-5 w-5 text-slate-500" />}
                </button>
                {openFaq === faq.question && (
                  <div className="px-6 pb-6">
                    <p className="text-slate-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-900 py-20 text-white" aria-label="Start with Zuri Voice">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Ready to transform your customer calls?</h2>
          <p className="mx-auto mt-3 max-w-2xl text-lg text-slate-300">
            Start your free trial today. No credit card required.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100 shadow-xl shadow-white/10"
          >
            Start Free Trial <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </>
  );
}
